import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { EMPTY, catchError, throwError } from 'rxjs';
import { ToastService } from '../toast/toast.service';
import { NavigationHubService } from '../navigation-hub/navigation-hub.service';
import { safeErrorSummary } from '../../helper/safe-error-summary';

/**
 * httpErrorInterceptor — functional replacement for the dead, never-registered
 * class-based `InterceptorService`.
 *
 * Every portal previously registered `authHttpInterceptorFn` ALONE, so failed
 * HTTP calls bubbled straight into NgRx stores whose error handlers did nothing
 * but flip `loading: false` — the user saw a frozen/empty screen with no signal
 * that anything failed.
 *
 * This interceptor gives every app a single, consistent place to translate
 * transport-level failures into user feedback:
 *  - 401 Unauthorized → route to login (re-auth signal). The request is
 *    swallowed (`EMPTY`) because we're navigating away.
 *  - 403 Forbidden → accessible "access denied" toast, then re-throw so the
 *    caller can still react.
 *  - 5xx → generic server-error toast, then re-throw.
 *  - everything else (4xx validation etc.) → re-thrown untouched so feature
 *    code keeps owning its specific messaging.
 *
 * Register LAST in `withInterceptors([...])` so it sees the response of inner
 * interceptors (auth token attach happens before the request goes out).
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const navHub = inject(NavigationHubService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          // Token expired / not authenticated — re-auth.
          navHub.routeToLogin();
          return EMPTY;
        }

        if (error.status === 403) {
          toast.error('You do not have access to that resource.');
        } else if (error.status >= 500) {
          toast.error('The server ran into a problem. Please try again shortly.');
        }
      } else if (isDevMode()) {
        // Non-HTTP failure inside the pipeline — surface in dev only.
        console.error('[httpErrorInterceptor] non-HTTP error', safeErrorSummary(error));
      }

      return throwError(() => error);
    }),
  );
};
